import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import {
  TIMED_SALE_BUYER_COMMISSION_PERCENTAGE,
  TIMED_SALE_SELLER_COMMISSION_PERCENTAGE,
  getBidUnitLabels,
  getListingBidQuantity,
  getTimedSaleFeeBreakdown,
  multiplyMoneyByQuantity,
} from '../../util/bidPricing';
import { normalizeMoney } from '../../util/genericHelpers';

import css from './AuctionPricingSummary.module.css';

export const formatAuctionMoney = (money, intl) => {
  if (!money?.currency || typeof money?.amount !== 'number') {
    return '-';
  }

  return intl.formatNumber(money.amount / 100, {
    style: 'currency',
    currency: money.currency,
    maximumFractionDigits: money.amount % 100 === 0 ? 0 : 2,
  });
};

const getHighestBidMoney = ({ userBids, marketplaceCurrency }) => {
  const bids = Array.isArray(userBids) ? userBids : [];
  const highestBid = bids.reduce((highest, bid) => {
    const amount = Number(bid?.biddingAmount);
    if (!Number.isFinite(amount)) return highest;
    if (!highest) return bid;
    return amount > Number(highest?.biddingAmount || 0) ? bid : highest;
  }, null);

  return normalizeMoney(
    highestBid
      ? { amount: Number(highestBid.biddingAmount), currency: highestBid.biddingCurrency }
      : null,
    marketplaceCurrency
  );
};

export const getAuctionPricingSummaryData = ({
  listing,
  userBids,
  publicData,
  marketplaceCurrency,
}) => {
  const unitLabels = getBidUnitLabels(listing);
  const quantity = getListingBidQuantity(listing);
  const highestBidMoney = getHighestBidMoney({ userBids, marketplaceCurrency });
  const reserveMoney = normalizeMoney(publicData?.reservePrice, marketplaceCurrency);
  const minBidIncrement = Number(publicData?.minBidIncrement);
  const minBidIncrementValue = Number.isFinite(minBidIncrement) ? minBidIncrement : 1;
  const minimumBidMoney = highestBidMoney
    ? normalizeMoney(
        {
          amount: Number(highestBidMoney.amount || 0) + minBidIncrementValue,
          currency: highestBidMoney.currency || marketplaceCurrency,
        },
        marketplaceCurrency
      )
    : normalizeMoney(publicData?.minimumBid || publicData?.startingBid, marketplaceCurrency) ||
      reserveMoney;

  const unitPrice = highestBidMoney || minimumBidMoney;
  const total = quantity > 1 && unitPrice ? multiplyMoneyByQuantity(unitPrice, quantity) : null;
  const hammerPrice = total || unitPrice;
  const timedSaleFees = getTimedSaleFeeBreakdown(hammerPrice);

  return {
    hasBids: Boolean(highestBidMoney),
    quantity,
    unitLabels,
    unitPrice,
    total,
    hammerPrice,
    timedSaleFees,
    showQuantityBreakdown: Boolean(quantity && quantity > 1 && unitPrice && total),
    showBreakdown: Boolean(hammerPrice && timedSaleFees),
  };
};

const AuctionPricingSummary = props => {
  const {
    className,
    listing,
    userBids,
    publicData,
    marketplaceCurrency,
    intl,
    showSellerCommission = false,
  } = props;
  const summary = getAuctionPricingSummaryData({
    listing,
    userBids,
    publicData,
    marketplaceCurrency,
  });

  if (!summary.showBreakdown) {
    return null;
  }

  const {
    hasBids,
    quantity,
    unitLabels,
    unitPrice,
    total,
    hammerPrice,
    timedSaleFees,
    showQuantityBreakdown,
  } = summary;
  const formattedUnitPrice = formatAuctionMoney(unitPrice, intl);
  const formattedTotal = formatAuctionMoney(total, intl);
  const formattedHammerPrice = formatAuctionMoney(hammerPrice, intl);
  const formattedBuyerCommission = formatAuctionMoney(timedSaleFees?.buyerCommission, intl);
  const formattedBuyerTotal = formatAuctionMoney(timedSaleFees?.buyerTotal, intl);
  const formattedSellerCommission = showSellerCommission
    ? formatAuctionMoney(timedSaleFees?.sellerCommission, intl)
    : null;
  const quantityUnitLabel = quantity === 1 ? unitLabels.singular : unitLabels.plural;

  return (
    <div className={classNames(css.root, className)}>
      {showQuantityBreakdown ? (
        <>
          <div className={css.row}>
            <span>
              <FormattedMessage
                id={
                  hasBids
                    ? 'OrderPanel.auctionCurrentBidPerUnitLabel'
                    : 'OrderPanel.auctionMinimumBidPerUnitLabel'
                }
                defaultMessage={
                  hasBids ? 'Current highest bid per {unitLabel}' : 'Minimum bid per {unitLabel}'
                }
                values={{ unitLabel: unitLabels.singular }}
              />
            </span>
            <strong className={css.value}>{formattedUnitPrice}</strong>
          </div>
          <div className={css.row}>
            <span>
              <FormattedMessage
                id="OrderPanel.auctionQuantityAvailableLabel"
                defaultMessage="Quantity available"
              />
            </span>
            <strong className={css.value}>
              <FormattedMessage
                id="OrderPanel.auctionQuantityAvailableValue"
                defaultMessage="{quantity} {unitLabel}"
                values={{ quantity, unitLabel: quantityUnitLabel }}
              />
            </strong>
          </div>
          <div className={classNames(css.row, css.sectionRow)}>
            <span>
              <FormattedMessage
                id={
                  hasBids
                    ? 'OrderPanel.auctionTotalAtCurrentBidLabel'
                    : 'OrderPanel.auctionTotalAtMinimumBidLabel'
                }
                defaultMessage={hasBids ? 'Total at current bid' : 'Total at minimum bid'}
              />
            </span>
            <strong className={css.value}>
              <FormattedMessage
                id="OrderPanel.auctionTotalBreakdownValue"
                defaultMessage="{unitPrice} x {quantity} = {total}"
                values={{ unitPrice: formattedUnitPrice, quantity, total: formattedTotal }}
              />
            </strong>
          </div>
        </>
      ) : (
        <div className={css.row}>
          <span>
            <FormattedMessage
              id="OrderPanel.auctionHammerPriceLabel"
              defaultMessage="Hammer price"
            />
          </span>
          <strong className={css.value}>{formattedHammerPrice}</strong>
        </div>
      )}
      <div className={classNames(css.row, showQuantityBreakdown ? null : css.sectionRow)}>
        <span>
          <FormattedMessage
            id="OrderPanel.auctionBuyerCommissionLabel"
            defaultMessage="Buyer commission ({percentage}%)"
            values={{ percentage: TIMED_SALE_BUYER_COMMISSION_PERCENTAGE }}
          />
        </span>
        <strong className={css.value}>{formattedBuyerCommission}</strong>
      </div>
      <div className={classNames(css.row, css.totalRow)}>
        <span>
          <FormattedMessage
            id="OrderPanel.auctionBuyerTotalLabel"
            defaultMessage="Estimated buyer total"
          />
        </span>
        <strong className={classNames(css.value, css.totalValue)}>{formattedBuyerTotal}</strong>
      </div>
      {showSellerCommission ? (
        <div className={classNames(css.row, css.sellerFeeRow)}>
          <span>
            <FormattedMessage
              id="OrderPanel.auctionSellerCommissionLabel"
              defaultMessage="Seller commission ({percentage}%, deducted from seller proceeds)"
              values={{ percentage: TIMED_SALE_SELLER_COMMISSION_PERCENTAGE }}
            />
          </span>
          <strong className={css.value}>{formattedSellerCommission}</strong>
        </div>
      ) : null}
    </div>
  );
};

export default AuctionPricingSummary;
